/**
 * Production calculation utilities for STAREK
 * Based on transition detection from signal data
 */

import { isBitSet } from "../utils";

/**
 * Detects high-to-low transitions in signal data
 * A high→low transition indicates one unit was produced
 *
 * @param {Array} datapoints - Array of {time, measure_value} sorted by time DESC
 * @param {Object} signal - Signal definition with type
 * @param {number} threshold - For analog signals, values above this are "high" (default 50% of max)
 * @param {Object} types - StarekContext types for bitfield decoding
 * @returns {Array} Array of transition timestamps
 */
export function detectTransitions(
    datapoints,
    signal,
    threshold = null,
    types = null,
) {
    if (!datapoints || datapoints.length < 2) return [];

    // Determine if signal is digital or analog using bitfield
    let isDigital = false;
    if (types?.device?.flags && typeof signal?.type === "number") {
        const flags = types.device.flags;
        const value = signal.type;
        isDigital = isBitSet(value, flags.BIT_LOCATION_DIGITAL);
    } else {
        // Fallback: check string type (legacy)
        isDigital =
            signal?.type === "digital_input" ||
            signal?.type === "digital_output";
    }

    console.log(
        `detectTransitions - signal ${signal.name}: isDigital=${isDigital}, type=${signal.type}, datapoints=${datapoints.length}`,
    );

    console.log(
        `detectTransitions - signal ${signal.name}: isDigital=${isDigital}, type=${signal.type}, datapoints=${datapoints.length}`,
    );

    const transitions = [];
    // Sort by time ascending for easier processing
    const sorted = [...datapoints].sort((a, b) => a.time - b.time);

    for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];

        let prevState, currState;

        if (isDigital) {
            // Digital: 0 = ON (high), 255 = OFF (low)
            prevState = parseInt(prev.measure_value) === 0 ? "high" : "low";
            currState = parseInt(curr.measure_value) === 0 ? "high" : "low";
        } else {
            // Analog: use threshold (default 50% or custom)
            const thresholdValue = threshold !== null ? threshold : 512; // 50% of 1024
            prevState =
                parseFloat(prev.measure_value) > thresholdValue
                    ? "high"
                    : "low";
            currState =
                parseFloat(curr.measure_value) > thresholdValue
                    ? "high"
                    : "low";
        }

        // Detect high→low transition (production unit completed)
        if (prevState === "high" && currState === "low") {
            console.log(
                `detectTransitions - ${signal.name}: transition at time ${curr.time}, value ${prev.measure_value} → ${curr.measure_value}`,
            );
            transitions.push({
                timestamp: curr.time,
                value: curr.measure_value,
            });
        }
    }

    console.log(
        `detectTransitions - signal ${signal.name}: found ${transitions.length} transitions`,
    );
    return transitions;
}

/**
 * Maps transitions to job schedules based on time windows
 *
 * @param {Array} transitions - Array of {timestamp} from detectTransitions
 * @param {Array} jobSchedules - Array of job schedule objects with start_time, end_time
 * @returns {Object} Map of jobSchedule.id → transition count
 */
export function mapTransitionsToJobs(transitions, jobSchedules) {
    const jobTransitionMap = {};

    // Initialize all jobs with 0
    jobSchedules.forEach((job) => {
        jobTransitionMap[job.id] = {
            count: 0,
            part: job.part,
            cycle: job.cycle,
            jobName: job.name,
        };
    });

    // Count transitions for each job's time window
    transitions.forEach((transition) => {
        const transitionTime = transition.timestamp * 1000; // Convert to ms

        jobSchedules.forEach((job) => {
            const startTime = new Date(job.start_time).getTime();
            const endTime = new Date(job.end_time).getTime();

            if (transitionTime >= startTime && transitionTime <= endTime) {
                jobTransitionMap[job.id].count++;
            }
        });
    });

    return jobTransitionMap;
}

/**
 * Fetches datapoints from API for given devices and time range
 *
 * @param {Object} params
 * @param {Array} params.deviceIds - Array of device IDs
 * @param {number} params.startTime - Unix timestamp
 * @param {number} params.endTime - Unix timestamp
 * @param {Object} params.workspace - Workspace with engine config
 * @param {string} params.authToken - Bearer token
 * @returns {Promise<Object>} API response data
 */
export async function fetchDatapoints({
    deviceIds,
    startTime,
    endTime,
    workspace,
    authToken,
}) {
    const { scheme, host, port } = workspace.engine;
    const workspaceId = workspace.name;
    const url = `${scheme}://${host}:${port}/api/v1/${workspaceId}/datapoints`;

    const payload = {
        devices: deviceIds,
        start_time: startTime,
        end_time: endTime,
        group_by_device: true,
        group_by_signal: true,
    };

    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch datapoints: ${response.status}`);
    }

    return await response.json();
}

/**
 * Calculates production data for devices with job schedules
 *
 * @param {Array} devices - Array of shadow devices with jobSchedules
 * @param {Object} datapointsData - Response from fetchDatapoints
 * @param {Object} types - StarekContext types for bitfield decoding
 * @returns {Array} Devices with calculated production data
 */
export function calculateProductionData(devices, datapointsData, types = null) {
    if (!datapointsData?.success || !datapointsData?.data) {
        return devices.map((dev) => ({
            ...dev,
            productionCalculated: false,
            signals: [],
            jobSchedules: (dev.jobSchedules || []).map((job) => ({
                ...job,
                unitsProduced: 0,
                transitions: [],
            })),
        }));
    }

    return devices.map((device) => {
        const deviceSignalData = datapointsData.data[device.id] || {};
        const runtimeSignals = device.device?.runtime?.signals || [];
        const signalsArray = Array.isArray(runtimeSignals) ? runtimeSignals : [];

        // Get GPIO INPUT signals only (production detection)
        const inputSignals = signalsArray.filter((sig) => {
            if (!types?.device?.flags) {
                console.warn("Types not available for bitfield checking, using name-based filter");
                return !sig.name.startsWith("/virt/");
            }
            const flags = types.device.flags;
            const value = sig.type;
            const isInput = isBitSet(value, flags.BIT_LOCATION_INPUT);
            const loBusBit = isBitSet(value, flags.BIT_LOCATION_LO_BUS) ? 1 : 0;
            const hiBusBit = isBitSet(value, flags.BIT_LOCATION_HI_BUS) ? 1 : 0;
            const busBits = (hiBusBit << 1) | loBusBit;
            return isInput && busBits === 0; // GPIO bus only
        });

        console.log(
            `calculateProductionData - device ${device.name}: found ${inputSignals.length} input signals:`,
            inputSignals.map((s) => s.name),
        );

        const jobSchedules = device.jobSchedules || [];

        // Track transitions per signal independently
        const signalTransitionsMap = {};
        const allTransitions = [];

        inputSignals.forEach((signal) => {
            const signalData = deviceSignalData[signal.name] || [];
            console.log(`calculateProductionData - signal ${signal.name}: ${signalData.length} datapoints`);
            const transitions = detectTransitions(signalData, signal, null, types);
            console.log(`calculateProductionData - signal ${signal.name}: ${transitions.length} transitions`);
            signalTransitionsMap[signal.name] = transitions;
            allTransitions.push(...transitions);
        });

        allTransitions.sort((a, b) => a.timestamp - b.timestamp);

        // Build per-signal breakdown with per-job unit counts
        const signals = inputSignals.map((signal) => {
            const transitions = signalTransitionsMap[signal.name];
            const jobMap = mapTransitionsToJobs(transitions, jobSchedules);

            let isDigital = false;
            if (types?.device?.flags && typeof signal.type === "number") {
                isDigital = isBitSet(signal.type, types.device.flags.BIT_LOCATION_DIGITAL);
            } else {
                isDigital = signal.type === "digital_input" || signal.type === "digital_output";
            }

            return {
                name: signal.name,
                type: signal.type,
                isDigital,
                totalUnitsProduced: transitions.length,
                jobBreakdown: jobSchedules.map((job) => ({
                    jobId: job.id,
                    jobName: job.name,
                    startTime: job.start_time,
                    endTime: job.end_time,
                    part: job.part,
                    cycle: job.cycle,
                    unitsProduced: jobMap[job.id]?.count || 0,
                })),
            };
        });

        // Also keep the merged job-level data for backwards compatibility
        const mergedJobMap = mapTransitionsToJobs(allTransitions, jobSchedules);
        const enhancedJobSchedules = jobSchedules.map((job) => ({
            ...job,
            unitsProduced: mergedJobMap[job.id]?.count || 0,
            transitions: allTransitions.filter((t) => {
                const tTime = t.timestamp * 1000;
                const startTime = new Date(job.start_time).getTime();
                const endTime = new Date(job.end_time).getTime();
                return tTime >= startTime && tTime <= endTime;
            }),
        }));

        console.log(`calculateProductionData - device ${device.name}: total ${allTransitions.length} transitions across ${signals.length} signals`);

        return {
            ...device,
            productionCalculated: true,
            signals,
            jobSchedules: enhancedJobSchedules,
            totalUnitsProduced: allTransitions.length,
        };
    });
}

/**
 * Gets the primary production signal for a device
 * Prioritizes digital inputs, then analog inputs
 *
 * @param {Object} device - Shadow device with runtime signals
 * @param {Object} types - StarekContext types for bitfield decoding
 * @returns {Object|null} Primary signal or null
 */
export function getPrimaryProductionSignal(device, types = null) {
    const signals = device.device?.runtime?.signals || [];
    // Ensure signals is an array
    const signalsArray = Array.isArray(signals) ? signals : [];

    if (!types?.device?.flags) {
        // Fallback: use legacy string type matching (or name-based filtering)
        const digitalInput = signalsArray.find(
            (s) => s.type === "digital_input",
        );
        if (digitalInput) return digitalInput;

        const analogInput = signalsArray.find((s) => s.type === "analog_input");
        if (analogInput) return analogInput;

        return null;
    }

    const flags = types.device.flags;

    // Priority: digital_input > analog_input
    const digitalInput = signalsArray.find((sig) => {
        const value = sig.type;
        const isDigital = isBitSet(value, flags.BIT_LOCATION_DIGITAL);
        const isInput = isBitSet(value, flags.BIT_LOCATION_INPUT);
        const loBusBit = isBitSet(value, flags.BIT_LOCATION_LO_BUS) ? 1 : 0;
        const hiBusBit = isBitSet(value, flags.BIT_LOCATION_HI_BUS) ? 1 : 0;
        const busBits = (hiBusBit << 1) | loBusBit;
        const isGPIO = busBits === 0;
        return isDigital && isInput && isGPIO;
    });
    if (digitalInput) return digitalInput;

    const analogInput = signalsArray.find((sig) => {
        const value = sig.type;
        const isAnalog = isBitSet(value, flags.BIT_LOCATION_ANALOG);
        const isInput = isBitSet(value, flags.BIT_LOCATION_INPUT);
        const loBusBit = isBitSet(value, flags.BIT_LOCATION_LO_BUS) ? 1 : 0;
        const hiBusBit = isBitSet(value, flags.BIT_LOCATION_HI_BUS) ? 1 : 0;
        const busBits = (hiBusBit << 1) | loBusBit;
        const isGPIO = busBits === 0;
        return isAnalog && isInput && isGPIO;
    });
    if (analogInput) return analogInput;

    return null;
}
